import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import React from "react";
import { useSelector } from "react-redux";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: "none",
    fontSize: theme.typography.pxToRem(12),
    border: "none",
    padding: 0,
    boxShadow: "none",
  },
}));

const SummaryCards = () => {
  const { dashboardData, loading, error } = useSelector(
    (state) => state.dashboard
  );

  if (loading) return <p>Loading summary cards...</p>;
  if (error) return <p>Error loading summary data: {error}</p>;
  if (!dashboardData) return null;

  // Card colors matching the breedsChart style
  const cardColors = ["#004f9e", "#9ca9ff", "#dee6ff", "#c0d4ff"];

  // ============================================
  // CUSTOM TOOLTIP DATA FUNCTION
  // Customize the additional data shown on hover for each card
  // ============================================
  const getTooltipContent = (cardTitle, dashboardData, cardIndex) => {
    let additionalData = [];

    // Customize additional data based on card title
    switch (cardTitle) {
      case "Active Pets":
        // Add custom fields for Active Pets
        // Example:
        // additionalData.push({ label: "Previous Period", value: dashboardData?.statCardsSummary?.active?.previous12MonthsValue ?? 0 });
        // additionalData.push({ label: "Growth", value: "5.2%" });
        break;

      case "Inactive Pets":
        // Add custom fields for Inactive Pets
        // Example:
        // additionalData.push({ label: "Previous Period", value: dashboardData?.statCardsSummary?.inactive?.previous12MonthsValue ?? 0 });
        break;

      case "New Pets":
        // Add custom fields for New Pets
        // Example:
        // additionalData.push({ label: "Previous Period", value: dashboardData?.statCardsSummary?.newPets?.previous12MonthsValue ?? 0 });
        break;

      case "Reactivation Count":
        // Add custom fields for Reactivation Count
        // Example:
        // additionalData.push({ label: "Previous Period", value: dashboardData?.reactivationSummary?.previousReactivationCount ?? 0 });
        break;

      default:
        break;
    }

    // Add more custom fields here following the same pattern:
    // additionalData.push({ label: "Your Label", value: "Your Value" });

    const color = cardColors[cardIndex] || "#004f9e";

    // Format tooltip matching breedsChart style exactly
    return (
      <div>
        {/* Header with gray background matching breedsChart */}
        <div
          className="apexcharts-tooltip-title"
          style={{
            fontFamily: "Helvetica, Arial, sans-serif",
            fontSize: "12px",
            backgroundColor: "#EBEBEB",
            color: "#000",
            padding: "8px",
            margin: 0,
          }}
        >
          {cardTitle}
          {/* <span
            className="apexcharts-tooltip-marker"
            style={{
              backgroundColor: color,
              marginLeft: "5px",
            }}
          /> */}
        </div>
        {/* Data section with white background matching breedsChart */}
        {additionalData?.length > 0 ? (
          <div
            className="apexcharts-tooltip-series-group"
            style={{
              order: 1,
              display: "flex",
              backgroundColor: "#fff",
              padding: "8px",
            }}
          >
            <div
              className="apexcharts-tooltip-text"
              style={{
                fontFamily: "Helvetica, Arial, sans-serif",
                fontSize: "12px",
                color: "#000",
              }}
            >
              {additionalData?.map((item, index) => (
                <div key={index} className="apexcharts-tooltip-y-group">
                  <span className="apexcharts-tooltip-text-y-label">
                    {item.label}{" "}
                  </span>
                  <span className="apexcharts-tooltip-text-y-value">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="apexcharts-tooltip-series-group"
            style={{
              order: 1,
              display: "flex",
              backgroundColor: "#fff",
              padding: "8px",
            }}
          >
            <div
              className="apexcharts-tooltip-text"
              style={{
                fontFamily: "Helvetica, Arial, sans-serif",
                fontSize: "12px",
                color: "#000",
              }}
            >
              {(() => {
                // Get data based on card title
                let data = null;
                if (cardTitle === "Active Pets") {
                  data = dashboardData?.statCardsSummary?.active;
                } else if (cardTitle === "Inactive Pets") {
                  data = dashboardData?.statCardsSummary?.inactive;
                } else if (cardTitle === "New Pets") {
                  data = dashboardData?.statCardsSummary?.newPets;
                } else if (cardTitle === "Reactivation Count") {
                  return (
                    <>
                      <div className="apexcharts-tooltip-y-group">
                        <span className="apexcharts-tooltip-text-y-label">
                          Reactivation Total:{" "}
                        </span>
                        <span className="apexcharts-tooltip-text-y-value">
                          {dashboardData?.reactivationSummary
                            ?.reactivationCount ?? 0}
                        </span>
                      </div>
                      <div className="apexcharts-tooltip-y-group">
                        <span className="apexcharts-tooltip-text-y-label">
                          Reactivation Percentage:{" "}
                        </span>
                        <span className="apexcharts-tooltip-text-y-value">
                          {dashboardData?.reactivationSummary
                            ?.reactivationPercentage ?? 0}
                        </span>
                      </div>
                    </>
                  );
                }

                if (data) {
                  return (
                    <>
                      <div className="apexcharts-tooltip-y-group">
                        <span className="apexcharts-tooltip-text-y-label">
                          Current 12 Months Value:{" "}
                        </span>
                        <span className="apexcharts-tooltip-text-y-value">
                          {data?.current12MonthsValue ?? 0}
                        </span>
                      </div>
                      <div className="apexcharts-tooltip-y-group">
                        <span className="apexcharts-tooltip-text-y-label">
                          Last 12 Months Value:{" "}
                        </span>
                        <span className="apexcharts-tooltip-text-y-value">
                          {data?.last12MonthsValue ?? 0}
                        </span>
                      </div>
                      <div className="apexcharts-tooltip-y-group">
                        <span className="apexcharts-tooltip-text-y-label">
                          Delta Percentage:{" "}
                        </span>
                        <span className="apexcharts-tooltip-text-y-value">
                          {data?.deltaPercentage ?? 0}
                        </span>
                      </div>
                    </>
                  );
                }

                return (
                  <div className="apexcharts-tooltip-y-group">
                    <span className="apexcharts-tooltip-text-y-value">
                      No additional data available
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    );
  };

  const cards = [
    {
      title: "Active Pets",
      value: dashboardData?.statCardsSummary?.active?.current12MonthsValue ?? 0,
    },
    {
      title: "Inactive Pets",
      value:
        dashboardData?.statCardsSummary?.inactive?.current12MonthsValue ?? 0,
    },
    {
      title: "New Pets",
      value:
        dashboardData?.statCardsSummary?.newPets?.current12MonthsValue ?? 0,
    },
    {
      title: "Reactivation Count",
      value: dashboardData?.reactivationSummary?.reactivationCount,
      percentage: dashboardData?.reactivationSummary?.reactivationPercentage,
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: "20px" }}>
        {cards?.map((card, i) => {
          const tooltipContent = getTooltipContent(
            card.title,
            dashboardData,
            i
          );
          return (
            <HtmlTooltip
              key={i}
              title={tooltipContent}
              arrow
              followCursor
              placement="top"
            >
              <div
                style={{
                  padding: "1px 20px 20px 20px",
                  background: "#e9f2ff",
                  borderRadius: "16px",
                  flex: 1,
                }}
              >
                <h4>{card?.title}</h4>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h2 style={{ margin: 0 }}>{card?.value}</h2>
                  {card?.percentage && (
                    <h4 style={{ margin: 0 }}>{card?.percentage}</h4>
                  )}
                </div>
              </div>
            </HtmlTooltip>
          );
        })}
      </div>
      <div className="mt15 gray4 fs14 font-medium">
        <span className="red-color">* </span>
        For 12 months Period
      </div>
    </div>
  );
};

export default SummaryCards;
